import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import downloadGPTIcon from '../images/svgLatest/downloadGPTIcon.svg';

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Extract a human-readable filename from a URL */
function extractFileName(url) {
  if (!url) return 'attachment';
  try {
    const pathname = new URL(url).pathname;
    const decoded = decodeURIComponent(pathname.split('/').pop());
    return decoded || 'attachment';
  } catch {
    return (url.split('/').pop()) || 'attachment';
  }
}

// ─── cache helper ─────────────────────────────────────────────────────────────

/**
 * Two-level cache:
 *   window._agentAttachmentCache    : Map<uuid, { status, blob, fileName, url }>
 *   window._agentAttachmentUrlIndex : Map<url,  uuid>
 *
 * Keying by UUID (not URL) ensures that files with the same name or the same
 * temporary URL never overwrite each other's cache entries.
 */

function initCache() {
  if (!window._agentAttachmentCache)    window._agentAttachmentCache    = new Map();
  if (!window._agentAttachmentUrlIndex) window._agentAttachmentUrlIndex = new Map();
}

/** Return the cache entry for a given URL (or null if not found). */
export function getAttachmentCacheEntry(url) {
  initCache();
  const id = window._agentAttachmentUrlIndex.get(url);
  return id ? window._agentAttachmentCache.get(id) : null;
}

/**
 * Start a background fetch, keyed by a fresh UUID.
 * A URL→UUID index entry is created immediately so repeated calls are no-ops.
 */
export function fetchAndCacheAttachment(url, fileName) {
  initCache();
  if (window._agentAttachmentUrlIndex.has(url)) return; // already in flight or done

  const id = uuidv4();
  window._agentAttachmentUrlIndex.set(url, id);
  window._agentAttachmentCache.set(id, { status: 'fetching', blob: null, fileName, url });

  fetch(url)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })
    .then(blob => {
      window._agentAttachmentCache.set(id, { status: 'ready', blob, fileName, url });
    })
    .catch((err) => {
      console.warn('[AgentAttachmentCard] fetch failed:', err);
      window._agentAttachmentCache.set(id, { status: 'failed', blob: null, fileName, url });
    });
}

/** Trigger a browser file-save download from a cached Blob */
function downloadBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the download can start
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

const DownloadProgressSpinner = ({ progress = 0 }) => {
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const filled = (clampedProgress / 100) * circumference;

  return (
    <svg className="agent-attachment-inline-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
      <circle cx="12" cy="12" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <circle
        cx="12"
        cy="12"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
};

function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${hours}:${minutes} ${ampm}`;
}

// ─── component ────────────────────────────────────────────────────────────────

const AgentAttachmentCard = ({ content,timestamp }) => {
  // Snapshot URL and filename once per card instance so later agentattachment events
  // do not overwrite existing cards via mutable window.config fallbacks.
  const resolvedUrlRef = useRef(
    (content && content.url) ||
    (window.config && window.config.attachMentUrl) ||
    ''
  );
  const url = resolvedUrlRef.current;

  const resolvedFileNameRef = useRef(
    (content && content.fileName) ||
    (window.config && window.config.attachMentFileName) ||
    extractFileName(url)
  );
  const fileName = resolvedFileNameRef.current;

  const [cacheStatus, setCacheStatus] = useState(() => {
    if (!url) return 'fetching';
    const entry = getAttachmentCacheEntry(url);
    return entry ? entry.status : 'fetching';
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const pollRef = useRef(null);
  const progressTimerRef = useRef(null);

  // Poll the cache map until fetch completes
  useEffect(() => {
    if (cacheStatus !== 'fetching') return;

    pollRef.current = setInterval(() => {
      const entry = getAttachmentCacheEntry(url);
      if (entry && entry.status !== 'fetching') {
        setCacheStatus(entry.status);
        clearInterval(pollRef.current);
      }
    }, 400);

    return () => clearInterval(pollRef.current);
  }, [url, cacheStatus]);

  const clearProgressTimer = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearProgressTimer();
  }, []);

  const startProgress = () => {
    clearProgressTimer();
    setIsDownloading(true);
    setDownloadProgress(5);
    progressTimerRef.current = setInterval(() => {
      setDownloadProgress(current => (current >= 90 ? current : current + 5));
    }, 150);
  };

  const completeProgress = () => {
    clearProgressTimer();
    setDownloadProgress(100);
    setTimeout(() => {
      setIsDownloading(false);
      setDownloadProgress(0);
    }, 400);
  };

  const handleDownload = () => {
    const entry = getAttachmentCacheEntry(url);
    if (!(entry && entry.status === 'ready' && entry.blob)) return;

    startProgress();

    // Download strictly from cached blob.
    downloadBlob(entry.blob, fileName);

    completeProgress();
  };

  const handleLinkDownload = event => {
    event.preventDefault();
    if (!isFetching) {
      handleDownload();
    }
  };

  const isFetching = cacheStatus === 'fetching';

  // Guard: if no url was provided, nothing to render (placed after all hooks)
  if (!url) return null;

  // Display only filename per requirement.
  const fileInfo = fileName;
  const fallbackText = '';
  const messageParts = [];

  if (content && typeof content === 'object' && content.text) {
    messageParts.push(content.text);
  }

  if (content && typeof content === 'object' && content.fileText) {
    messageParts.push(content.fileText);
  }

  const messageText = messageParts.length ? messageParts.join(' ') : fallbackText;
  const agentName = content.label || 'Agent';
  const timeText = formatAMPM(new Date(timestamp));
   
  return (
      <div className="agentIcon">
           <div className="agentIconDiv">
            <div className="botTextBox conversationTextBox">
              <div className="conv-text">
                {/* <p>{messageText}</p> */}
                <a
                  href="#"
                  className={`agent-attachment-reference-link${isDownloading ? ' download-in-progress' : ''}`}
                  title={fileInfo}
                  onClick={handleLinkDownload}
                  aria-disabled={isFetching}
                  aria-busy={isDownloading}
                >
                  <span className="agent-attachment-file-name">{fileInfo}</span>
                  {isDownloading && (
                    <span className="agent-attachment-progress" aria-live="polite">
                      <DownloadProgressSpinner progress={downloadProgress} />
                    </span>
                  )}
                  <img className="agent-attachment-file-icon" alt="open" src={downloadGPTIcon} />
                </a>
              </div>
            </div>
            <div className="botTimestamp">
              <span>{agentName} | {timeText}</span>
            </div>
            <div className="sendRetry">
              <span>Send Failed. </span>
              <a className="markdown-link" href="#" onClick={(event) => event.preventDefault()}>
                Retry.
              </a>
            </div>
          </div>
      </div>
  );
};

export default AgentAttachmentCard;
